import {useApolloClient} from '@apollo/client'
import {useParams, useSearchParams} from 'react-router-dom'
import {useEffect} from 'react'

import {useFragmentHelpers, useIntegrationBlocksQuery} from '@sdox/api'

import {usePanels} from '../../../context/PanelContext'

import type {IntegrationQueryInput, XcNwBlock_BlockIntegrationType_Fragment} from '@sdox/api'

export interface UseLoadIntegrationParams {
  block: XcNwBlock_BlockIntegrationType_Fragment
  skip: boolean
}

export interface UseLoadIntegrationResult {
  loading: boolean
}

export const useLoadIntegration = ({block, skip}: UseLoadIntegrationParams): UseLoadIntegrationResult => {
  const client = useApolloClient()
  const [searchParams] = useSearchParams()
  const {seFolderSlug, documentId = '', clpSlug} = useParams()
  const [panel] = usePanels()
  const {updatePanel} = useFragmentHelpers()

  if (!panel) throw new Error('Programming error - no Panel set')

  let integrationInput: IntegrationQueryInput | undefined = undefined
  if (seFolderSlug){
    integrationInput = {
      clpInput: undefined,
      folderInput: {folderSlug: seFolderSlug, folderItemId: documentId},
    }
  }

  if (clpSlug){
    const bnr = searchParams.get('bnr') ?? '-1'
    integrationInput = {
      clpInput:  {clpSlug : clpSlug, bnr},
      folderInput: undefined,
    }
  }

  if (!integrationInput) throw new Error('Programming error - no seFolderSlug/clpSlug set')

  const {data, loading, error} = useIntegrationBlocksQuery({
    skip,
    variables: {
      integrationType: block.integrationType,
      defaultValue: block.defaultValue,
      integrationInput,
    },
    fetchPolicy: 'cache-first',
  })

  const hasError = error !== undefined

  // remove block from cache when integration can not be loaded
  // e.g. when no instagram API key is entered for organization -> don't show integration block
  useEffect(() => {
    if (hasError) {
      client.cache.evict({id: `${block.__typename}:${block.id}`})
    }
  }, [hasError, block.__typename, block.id, client])

  // replace integration block in panel with blocks from server
  useEffect(() => {
    if (data === undefined) return

    const newBlocks = [...panel.blocks]
    const insertIndex = newBlocks.findIndex(b => b.id === block.id)
    if (insertIndex === -1) return
    newBlocks.splice(insertIndex, 1, ...data.integrationBlocks)
    updatePanel(panel.id, {...panel, blocks: newBlocks})
    // updatePanel is not stable in useFragmentHelpers, so we should not include it in the dependencies array
    // this is not a problem though
    // eslint-disable-next-line
  }, [data, hasError, block, panel])

  return {loading}
}
